import { useCallback, useEffect, useState } from 'react'

/**
 * Sprachausgabe über die Web Speech API.
 *
 * Die Stimmenliste ist je nach Browser und Betriebssystem verschieden und lädt
 * asynchron. Deshalb wird geprüft statt gehofft: gibt es keine deutsche
 * Stimme, wird der Knopf ausgegraut und sagt warum — er scheitert nicht stumm.
 */

export type Verfuegbarkeit = 'wird_geprueft' | 'bereit' | 'keine_stimme' | 'nicht_unterstuetzt'

const WARTEZEIT_STIMMEN_MS = 2000

interface Stimmlage {
  verfuegbarkeit: Verfuegbarkeit
  stimme: SpeechSynthesisVoice | null
}

export interface Sprachausgabe {
  verfuegbarkeit: Verfuegbarkeit
  spricht: boolean
  tempo: number
  setzeTempo: (wert: number) => void
  sprich: (text: string) => void
  stopp: () => void
}

function unterstuetzt(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

/**
 * Liest den aktuellen Stand der Stimmenliste.
 *
 * Wird auch beim ersten Rendern aufgerufen — sonst müsste ein Effekt den
 * Zustand sofort wieder setzen, und genau das erzeugt Kaskadenrenderings.
 */
function ermittleStimmlage(): Stimmlage {
  if (!unterstuetzt()) return { verfuegbarkeit: 'nicht_unterstuetzt', stimme: null }

  const stimmen = window.speechSynthesis.getVoices()
  if (stimmen.length === 0) return { verfuegbarkeit: 'wird_geprueft', stimme: null }

  const deutsch = stimmen.find((s) => s.lang.toLowerCase().startsWith('de')) ?? null
  return { verfuegbarkeit: deutsch ? 'bereit' : 'keine_stimme', stimme: deutsch }
}

export function useSprachausgabe(): Sprachausgabe {
  const [stimmlage, setStimmlage] = useState<Stimmlage>(ermittleStimmlage)
  const [spricht, setSpricht] = useState(false)
  const [tempo, setzeTempo] = useState(1)

  useEffect(() => {
    if (!unterstuetzt()) return

    const beiAenderung = () => {
      setStimmlage(ermittleStimmlage())
    }
    window.speechSynthesis.addEventListener('voiceschanged', beiAenderung)

    // Manche Browser feuern `voiceschanged` nie. Ohne diese Frist bliebe der
    // Knopf für immer im Zustand „wird geprüft".
    const frist = window.setTimeout(() => {
      setStimmlage((bisher) =>
        bisher.verfuegbarkeit === 'wird_geprueft'
          ? { verfuegbarkeit: 'keine_stimme', stimme: null }
          : bisher,
      )
    }, WARTEZEIT_STIMMEN_MS)

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', beiAenderung)
      window.clearTimeout(frist)
      window.speechSynthesis.cancel()
    }
  }, [])

  const stopp = useCallback(() => {
    if (!unterstuetzt()) return
    window.speechSynthesis.cancel()
    setSpricht(false)
  }, [])

  const sprich = useCallback(
    (text: string) => {
      if (!unterstuetzt()) return
      window.speechSynthesis.cancel()

      const aeusserung = new SpeechSynthesisUtterance(text)
      aeusserung.lang = stimmlage.stimme?.lang ?? 'de-DE'
      if (stimmlage.stimme) aeusserung.voice = stimmlage.stimme
      aeusserung.rate = tempo
      aeusserung.onend = () => {
        setSpricht(false)
      }
      aeusserung.onerror = () => {
        setSpricht(false)
      }

      setSpricht(true)
      window.speechSynthesis.speak(aeusserung)
    },
    [stimmlage.stimme, tempo],
  )

  return { verfuegbarkeit: stimmlage.verfuegbarkeit, spricht, tempo, setzeTempo, sprich, stopp }
}
